<div class="h-full flex flex-col gap-2 z-0 relative overflow-y-auto p-2 lg:pl-0">
  <div
    class="max-w-6xl p-4 rounded-lg bg-white dark:bg-zinc-950 border border-zinc-950/10 dark:border-white/10 shadow-lg">
    <form [formGroup]="payForm" (ngSubmit)="onSave()">
      <h1 class="heading">
        Pay Bill
      </h1>
      <hr class="my-4 w-full border-t border-zinc-950/10 dark:border-white/10" />

      <p *ngIf="bill && !bill.Id" class="mb-4 text-sm text-amber-700 dark:text-amber-400">
        This bill is not in QuickBooks yet. Push the bill first, then record a payment.
      </p>
      <section class="grid gap-4 sm:grid-cols-2">
        <div class="space-y-1">
          <label class="subheading">Vendor</label>
          <div>
            <input type="text" [value]="bill?.VendorRef?.name || ''" disabled />
          </div>
        </div>

        <div class="space-y-1">
          <label class="subheading">Bill No.</label>
          <div>
            <input type="text" [value]="bill?.DocNumber || ''" disabled />
          </div>
        </div>

        <div class="space-y-1">
          <label class="subheading">Total Amount</label>
          <div>
            <input type="text" [value]="bill?.TotalAmt || 0" disabled />
          </div>
        </div>

        <div class="space-y-1">
          <label class="subheading">Already Paid</label>
          <div>
            <input type="text" [value]="amountPaid" disabled />
          </div>
        </div>

        <div class="space-y-1">
          <label class="subheading">Balance</label>
          <div>
            <input type="text" [value]="remainingBalance" disabled />
          </div>
        </div>

        <div class="space-y-1">
          <label class="subheading after:text-red-600 after:content-['*']">Amount</label>
          <div>
            <input type="number" formControlName="TotalAmt" min="0.01" step="0.01" />
          </div>
          <div *ngIf="(submitted || form.TotalAmt.dirty) && form.TotalAmt.errors">
            <p *ngIf="form.TotalAmt.errors?.['required']" class="pt-1 text-red-600 text-xs">Amount is required</p>
            <p *ngIf="form.TotalAmt.errors?.['min']" class="pt-1 text-red-600 text-xs">Amount must be greater than 0</p>
            <p *ngIf="form.TotalAmt.errors?.['max']" class="pt-1 text-red-600 text-xs">
              Your payment is more than the remaining amount ({{ remainingBalance | currency:'USD':'symbol':'1.2-2' }})
            </p>
          </div>
        </div>

        <ng-container formGroupName="BankAccountRef">
          <div class="space-y-1">
            <label class="subheading after:text-red-600 after:content-['*']">Bank/Credit account</label>
            <div class="flex items-center gap-3">
              <div class="relative flex-1">
                <select formControlName="value" (change)="onAccountChange($event)" class="pr-10">
                  <option value="" disabled>Select a bank account</option>
                  <option *ngFor="let account of accounts" [value]="account.Id">
                    {{ account.Name }}
                  </option>
                </select>
                <svg class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </div>
              <div *ngIf="selectedAccount"
                class="w-44 rounded-lg border px-3 py-1.5 text-sm font-medium"
                [ngClass]="(selectedAccount.CurrentBalance || 0) < 0
                  ? 'bg-pink-50 border-pink-200 text-pink-700 dark:bg-pink-400/10 dark:border-pink-400/20 dark:text-pink-300'
                  : 'bg-green-50 border-green-200 text-green-700 dark:bg-green-400/10 dark:border-green-400/20 dark:text-green-300'">
                {{ (selectedAccount.CurrentBalance || 0) | currency:'USD':'symbol':'1.2-2' }}
              </div>
            </div>
            <div *ngIf="submitted && payForm.get('BankAccountRef.value')?.errors">
              <p class="pt-1 text-red-600 text-xs">Bank/Credit account is required</p>
            </div>
          </div>
        </ng-container>
      </section>

      <section *ngIf="bill">
        <h2 class="subheading mt-4">Transactions List</h2>
        <hr class="my-4 w-full border-t border-zinc-950/10 dark:border-white/10" />
        <div class="overflow-x-auto border border-zinc-200 dark:border-zinc-700 rounded-lg">
          <table class="min-w-full border border-zinc-300 dark:border-zinc-700 border-collapse">
            <thead class="bg-zinc-50 dark:bg-zinc-950">
              <tr>
                <th class="p-2 text-left text-sm-dark border border-zinc-300 dark:border-zinc-700">Date</th>
                <th class="p-2 text-left text-sm-dark border border-zinc-300 dark:border-zinc-700">Type</th>
                <th class="p-2 text-left text-sm-dark border border-zinc-300 dark:border-zinc-700">No.</th>
                <th class="p-2 text-left text-sm-dark border border-zinc-300 dark:border-zinc-700">Payee</th>
                <th class="p-2 text-right text-sm-dark border border-zinc-300 dark:border-zinc-700">Total</th>
                <th class="p-2 text-center text-sm-dark border border-zinc-300 dark:border-zinc-700">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let payment of billPayments">
                <td class="p-2 text-sm-light border border-zinc-300 dark:border-zinc-700">
                  {{ ((payment.TxnDate || payment.createdAt) | date: 'MM/DD/YYYY') || 'N/A' }}
                </td>
                <td class="p-2 text-sm-light border border-zinc-300 dark:border-zinc-700">
                  {{ payment.typeLabel }}
                </td>
                <td class="p-2 text-sm-light border border-zinc-300 dark:border-zinc-700">{{ payment.Id || 'Pending' }}</td>
                <td class="p-2 text-sm-light border border-zinc-300 dark:border-zinc-700">{{ payment.VendorRef?.name || bill?.VendorRef?.name || 'N/A' }}</td>
                <td class="p-2 text-sm-light border border-zinc-300 dark:border-zinc-700 text-right"
                  [class.text-red-600]="!!payment.Id && +payment.TotalAmt > 0"
                  [class.text-amber-600]="!payment.Id">
                  {{ !!payment.Id && +payment.TotalAmt > 0 ? '-' + payment.TotalAmt : payment.TotalAmt || 0 }}
                </td>
                <td class="px-2 py-1 text-sm-light border border-zinc-300 dark:border-zinc-700 text-nowrap">
                  <div class="flex flex-nowrap justify-center items-center gap-x-2 text-xs leading-tight whitespace-nowrap">
                    <a *ngIf="canPushPayment(payment)" href="javascript:void(0)" (click)="onSyncPayment(payment)" class="text-sky-700 dark:text-sky-400 hover:underline">Push To</a>
                    <a *ngIf="canVoidPayment(payment)" href="javascript:void(0)" (click)="onVoidPayment(payment)" class="text-sky-700 dark:text-sky-400 hover:underline">Void</a>
                    <a href="javascript:void(0)" (click)="onDeletePayment(payment)" class="text-red-600 hover:underline">Delete</a>
                  </div>
                </td>
              </tr>
              <tr>
                <td class="p-2 text-sm-light border border-zinc-300 dark:border-zinc-700">
                  {{ ((bill.TxnDate || bill.createdAt) | date: 'MM/DD/YYYY') || 'N/A' }}
                </td>
                <td class="p-2 text-sm-light border border-zinc-300 dark:border-zinc-700">Bill</td>
                <td class="p-2 text-sm-light border border-zinc-300 dark:border-zinc-700">{{ bill.DocNumber || 'N/A' }}</td>
                <td class="p-2 text-sm-light border border-zinc-300 dark:border-zinc-700">{{ bill.VendorRef?.name || 'N/A' }}</td>
                <td class="p-2 text-sm-light border border-zinc-300 dark:border-zinc-700 text-right">
                  {{ bill.TotalAmt || 0 }}
                </td>
                <td class="p-2 text-sm-light border border-zinc-300 dark:border-zinc-700"></td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="4" class="p-2 text-sm-dark border border-zinc-300 dark:border-zinc-700 text-right">Total</td>
                <td class="p-2 text-sm-dark border border-zinc-300 dark:border-zinc-700 text-right">{{ remainingBalance }}</td>
                <td class="p-2 text-sm-dark border border-zinc-300 dark:border-zinc-700"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <hr class="my-4 w-full border-t border-zinc-950/10 dark:border-white/10" />
      <section class="flex justify-end gap-4">
        <button type="submit" class="btn btn-secondary" [disabled]="!bill?.Id">Pay</button>
      </section>
    </form>
  </div>
</div>
